
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle, Clock, DollarSign } from "lucide-react";
import { SubscriptionType } from "@/lib/supabase/types";
import { format } from "date-fns";

interface UpdateSubscriptionPaymentDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: SubscriptionType | null;
  onUpdatePaymentStatus: (subscriptionId: string, status: 'pending' | 'paid' | 'nptr') => void;
}

const UpdateSubscriptionPaymentDialog: React.FC<UpdateSubscriptionPaymentDialogProps> = ({
  isOpen,
  onOpenChange,
  subscription,
  onUpdatePaymentStatus
}) => {
  const [selectedStatus, setSelectedStatus] = React.useState<'pending' | 'paid' | 'nptr'>(
    (subscription?.payment_status as 'pending' | 'paid' | 'nptr') || 'pending'
  );

  React.useEffect(() => {
    if (subscription) {
      setSelectedStatus((subscription.payment_status as 'pending' | 'paid' | 'nptr') || 'pending');
    }
  }, [subscription]);

  const handleUpdateStatus = () => {
    if (!subscription) return;
    onUpdatePaymentStatus(subscription.id, selectedStatus);
  };

  const statusChanged = subscription?.payment_status !== selectedStatus;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Payment Status</DialogTitle>
        </DialogHeader>

        {subscription && (
          <div className="grid gap-6 py-4">
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-muted-foreground">Subscription for:</span>
              <span className="font-medium">{subscription.userName || "Unknown User"}</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-muted-foreground">
                {format(new Date(subscription.purchaseDate), 'MMM d, yyyy')}
              </span>
            </div>

            <RadioGroup
              value={selectedStatus}
              onValueChange={(value) => setSelectedStatus(value as 'pending' | 'paid' | 'nptr')}
              className="grid gap-4"
            >
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="pending" id="pending" className="mt-1" />
                <div className="grid gap-1.5 w-full">
                  <Label htmlFor="pending" className="flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-amber-500" />
                    Payment Pending
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    The payment process has started but hasn't been confirmed yet.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <RadioGroupItem value="paid" id="paid" className="mt-1" />
                <div className="grid gap-1.5 w-full">
                  <Label htmlFor="paid" className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                    Payment Completed
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    The payment has been successfully processed and confirmed.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <RadioGroupItem value="nptr" id="nptr" className="mt-1" />
                <div className="grid gap-1.5 w-full">
                  <Label htmlFor="nptr" className="flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2 text-red-500" />
                    Not Paid for Technical Reason (NPTR)
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    The payment couldn't be processed due to a technical issue.
                  </p>
                </div>
              </div>
            </RadioGroup>

            {selectedStatus === 'paid' && subscription.payment_status !== 'paid' && !subscription.active && (
              <div className="p-3 bg-green-50 border border-green-100 rounded-lg">
                <p className="text-sm text-green-800 flex items-center">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Marking as paid will automatically activate this subscription.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateStatus}
            disabled={!statusChanged}
          >
            Update Payment Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateSubscriptionPaymentDialog;
